import React, { useState, useEffect } from "react"; 
import PageHeader from "../../components/PageHeader";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Snackbar,
  Paper,
  Link as MuiLink,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LockReset, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axiosClient from "../../../axiosClient";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // --- URL Parameters ---
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");
  const cardNumber = searchParams.get("card_number");
  const customerId = searchParams.get("customer_id");

  // --- State ---
  const [formData, setFormData] = useState({ password: "", confirm_password: "" });
  const [touched, setTouched] = useState({ password: false, confirm_password: false });
  
  // Independent visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/", { replace: true });
    }
  }, [token, navigate]);
  
  if (!token) return null;
  

  // --- Validations ---
  const validatePassword = (pass) => pass && pass.length >= 6;
  const validateMatch = (p, c) => p === c && c.length > 0;
  const isValid = validatePassword(formData.password) && validateMatch(formData.password, formData.confirm_password);

  // --- Handlers ---
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleBlur = (e) => setTouched({ ...touched, [e.target.name]: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirm_password: true });

    if (!isValid) return;

    setLoading(true);
    setAlert({ type: "", message: "" });

    try {
      await axiosClient.post("/proxy/reset-password", {
        token,
        email: emailParam || null,
        card_number: cardNumber || null,
        customer_id: customerId || null,
        password: formData.password,
        password_confirmation: formData.confirm_password,
      });

      setAlert({ type: "success", message: "Password updated successfully!" });
      setSnackOpen(true);
      setFormData({ password: "", confirm_password: "" });
      
      // Redirect to home after success
      setTimeout(() => navigate("/"), 2500);

    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;

      if (status === 404 || status === 400) {
        setAlert({ type: "error", message: msg || "Invalid or expired link." });
         setTimeout(() => navigate("/", { replace: true }), 3000);
      } else {
        setAlert({ type: "error", message: msg || "An error occurred." });
      }
    } finally {
      setLoading(false);
    }
  };

  // Modern Input Styles 
  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fafafa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#b0b0b0" },
      "&.Mui-focused fieldset": { borderColor: "#00b5e2" },
    }
  };

  return (
    <>
      <PageHeader title="PASSWORD RESET" pages={[]} />
      
      <Box sx={{ py: 10, display: "flex", justifyContent: "center", backgroundColor: "#f4f6f8", minHeight: "80vh" }}>
        <Container maxWidth="sm">
          
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 3, md: 5 }, 
              borderRadius: "24px", 
              boxShadow: "0px 10px 40px rgba(0,0,0,0.04)", 
              backgroundColor: "#ffffff"
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box sx={{ 
                display: 'inline-flex', p: 1.5, borderRadius: '50%', 
                backgroundColor: '#e0f7fa', mb: 2, color: '#00b5e2' 
              }}>
                <LockReset sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a1c23", mb: 1 }}>
                Reset Your Password
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", lineHeight: 1.6 }}>
                Enter a new secure password for your account associated with <strong>{emailParam || "your email"}</strong>.
              </Typography>
            </Box>

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              
              {/* Email Field (Always visible, Read-Only) */}
              <TextField 
                label="Account Email" 
                value={emailParam || ""} 
                disabled 
                fullWidth 
                sx={{
                  ...inputStyles,
                  "& .MuiInputBase-root": { 
                      borderRadius: "12px",
                      backgroundColor: "#eef1f3", // Gray background for disabled state
                      color: "#666"
                  } 
                }} 
              />

              {/* Password Field */}
              <TextField
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && !validatePassword(formData.password)}
                helperText={touched.password && !validatePassword(formData.password) ? "Minimum 6 characters" : ""}
                sx={inputStyles}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password Field */}
              <TextField
                name="confirm_password"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirm_password && !validateMatch(formData.password, formData.confirm_password)}
                helperText={touched.confirm_password && !validateMatch(formData.password, formData.confirm_password) ? "Passwords do not match" : ""}
                sx={inputStyles}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {alert.message && (
                <Alert severity={alert.type} sx={{ borderRadius: "12px" }}>
                  {alert.message}
                </Alert>
              )}

              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || !isValid} 
                startIcon={<LockReset />} 
                sx={{ 
                  backgroundColor: "#00b5e2", 
                  borderRadius: "50px", 
                  py: 1.8, 
                  fontSize: "1rem", 
                  fontWeight: 700,
                  boxShadow: "0 4px 14px 0 rgba(0, 181, 226, 0.39)",
                  "&:hover": { 
                    backgroundColor: "#009fc7",
                    boxShadow: "0 6px 20px 0 rgba(0, 181, 226, 0.23)"
                  },
                  textTransform: 'none'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Update Password"}
              </Button>

              <Box sx={{ textAlign: "center", mt: 1 }}>
                <MuiLink component={Link} to="/" underline="hover" sx={{ color: "#9ca3af", fontWeight: 500 }}>
                  Return to Home
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
      <Snackbar open={snackOpen} autoHideDuration={3000} message="Password updated!" />
    </>
  );
};

export default ResetPassword;